import { FC, Fragment, useCallback, useMemo, useState } from 'react';

import { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import { ErrorBoundary } from '@zola/zola-ui/src/components/ErrorBoundary';
import Tabs from '@zola/zola-ui/src/components/Tabs/Tabs';
import { useEffectOnce } from '@zola/zola-ui/src/hooks';
import MEDIA_QUERY_V2 from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';

import styled from '@emotion/styled';

import { storefrontCardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import { doTrackStorefrontListViewed } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { isSearchLocation } from '~/util/searchUtils';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { ZolaTopPickCategory } from '../../types/types';
import { SmallVendorCardShelf } from '../RecentlyViewedStorefronts/RecentlyViewedStorefronts';
import { getCategoryHeading } from './PageHeading';

const TopPickLabels: Record<
  ZolaTopPickCategory,
  (vendorCategoryLabel: string, location: string) => string
> = {
  'top-picks': (vendorCategoryLabel) => `Best of Zola Wedding ${vendorCategoryLabel}`,
  unique: (vendorCategoryLabel, location) =>
    `Top Unique Wedding ${vendorCategoryLabel} in ${location}`,
  'all-inclusive': (vendorCategoryLabel, location) =>
    `Top All Inclusive Wedding ${vendorCategoryLabel} in ${location}`,
  'outdoor-option': (vendorCategoryLabel, location) =>
    `Top ${vendorCategoryLabel} with Outdoor options in ${location}`,
  'most-affordable': (vendorCategoryLabel, location) =>
    `Most affordable Wedding ${vendorCategoryLabel} in ${location}`,
};

const TopPickTrackingShelves: Record<ZolaTopPickCategory, string> = {
  'top-picks': 'SEARCH_RESULTS_PAGE_TOP_IN_LOCATION',
  unique: 'SEARCH_RESULTS_PAGE_TOP_UNIQUE',
  'all-inclusive': 'SEARCH_RESULTS_PAGE_TOP_ALL_INCLUSIVE',
  'outdoor-option': 'SEARCH_RESULTS_PAGE_TOP_OUTDOOR',
  'most-affordable': 'SEARCH_RESULTS_PAGE_TOP_MOST_AFFORDABLE',
};

const StyledCardShelf = styled(SmallVendorCardShelf)`
  padding: 16px 40px 0;

  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    padding-left: 16px;
  }

  .vendor-name {
    font-size: 14px !important;
  }
`;

const ZolaTopPicksPanelSmall = ({
  vendors,
  category,
}: {
  vendors: StorefrontCardView[];
  category: ZolaTopPickCategory;
}) => {
  return (
    <StyledCardShelf
      noBorder
      vendorCards={vendors.map(storefrontCardViewToVendorCardVendor)}
      showRatingCount={false}
      cardProps={{
        showLocation: false,
        showPricing: true,
        showDetails: true,
        venueDetailsConfig: {
          showCapacity: true,
          showServiceLevel: false,
          showSetting: false,
        },
        size: 'large',
      }}
      scrimLeft
      // @ts-expect-error listing shelf names not yet updated
      listingShelfName={TopPickTrackingShelves[category]}
    />
  );
};
export const ZolaTopPicks: React.FC<{ tabWrapper: FC }> = ({
  children,
  tabWrapper: TabWrapper = 'div',
}) => {
  const { zolaTopPicks, vendorSearch } = useSearchResultsPageContext();
  const { selectedTaxonomy, selectedLocation } = vendorSearch;

  const vendorCategoryLabel: string = useMemo(
    () => getCategoryHeading(selectedTaxonomy?.key),
    [selectedTaxonomy?.key]
  );

  const location: string = useMemo(
    () => (isSearchLocation(selectedLocation) ? selectedLocation.label : ''),
    [selectedLocation]
  );

  /** This state forces a re-draw of the tab contents when the tab is clicked.  */
  const [guid, setGuid] = useState<string>();

  const trackProductListViewed = useCallback(
    ({ category, vendors }: { category: ZolaTopPickCategory; vendors: StorefrontCardView[] }) => {
      doTrackStorefrontListViewed({
        storefrontList: vendors,
        taxonomyKey: vendorSearch.selectedTaxonomy?.key as SearchableVendorTaxonomyKey,
        /** @ts-expect-error Listing Shelf Name not yet added */
        listingShelfName: TopPickTrackingShelves[category],
        includeTags: false,
      });
    },
    [vendorSearch.selectedTaxonomy?.key]
  );

  useEffectOnce(() => {
    // Track product list viewed for the first tab
    if (zolaTopPicks.length > 0) {
      trackProductListViewed(zolaTopPicks[0]);
    }
  });

  const tabItems = useMemo(() => {
    return zolaTopPicks.map(({ category, vendors }) => {
      const fcn = TopPickLabels[category];
      return {
        id: category,
        text: fcn ? fcn(vendorCategoryLabel, location) : 'Error: ' + category,
        panelContent: <ZolaTopPicksPanelSmall vendors={vendors} category={category} />,
        onClick: () => {
          trackProductListViewed({ category, vendors });
          setGuid('' + Math.random());
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zolaTopPicks, vendorCategoryLabel, location, trackProductListViewed, guid]);

  if (zolaTopPicks.length === 0) {
    return <Fragment />;
  }

  return (
    <ErrorBoundary>
      <TabWrapper>
        <Tabs tabItems={tabItems} noScrollIntoView noVerticalScroll />
      </TabWrapper>
      {children}
    </ErrorBoundary>
  );
};
